import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import { hasPermission } from '../../utils/permissions';
import { AI_AGENTS } from '../../utils/agents';
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal";
import { toast } from "sonner"
import Button from "../Common/Button"

const { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSave, 
  FiX, 
  FiUpload, 
  FiLoader, 
  FiSettings, 
  FiImage, 
  FiCheck, 
  FiActivity, 
  FiGlobe, 
  FiClock,
  FiMail
} = FiIcons;

const AgentsManagementPage = ({ setActiveTab }) => {
  const { user, teams } = useAuth();
  const t = useTranslation(user?.language);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTeam, setSearchTeam] = useState('');
  const [agentForm, setAgentForm] = useState({
    id: '',
    name: '',
    description: '',
    webhook: '',
    icon: '🤖',
    image_url: '',
    is_active: true,
    training_data: '',
    request_email: ''
  });

  const isAdmin = hasPermission(user, 'canEditAgents');

  // Initialize default agents in database
  // const initializeDefaultAgents = async () => {
  //   try {
  //     console.log('Checking for default agents in database...');
  //     // Check if default agents exist
  //     const { data: existingAgents, error: fetchError } = await supabase
  //       .from('ai_agents_a1b2c3d4e5')
  //       .select('id')
  //       .in('id', Object.keys(AI_AGENTS));

  //     if (fetchError) throw fetchError;

  //     const existingIds = existingAgents?.map(a => a.id) || [];
  //     const missingAgents = Object.entries(AI_AGENTS).filter(([id]) => !existingIds.includes(id));

  //     if (missingAgents.length > 0) {
  //       console.log('Creating missing default agents:', missingAgents.map(([id]) => id));
  //       const agentsToInsert = missingAgents.map(([id, agent]) => ({
  //         id,
  //         name: agent.name,
  //         description: t(agent.descriptionKey) || agent.name,
  //         webhook: agent.webhook,
  //         icon: agent.icon,
  //         is_active: true,
  //         created_at: new Date().toISOString(),
  //         created_by: user.id
  //       }));

  //       const { error: insertError } = await supabase
  //         .from('ai_agents_a1b2c3d4e5')
  //         .insert(agentsToInsert);

  //       if (insertError) throw insertError;
  //       console.log('Default agents created successfully');

  //       toast.success(t('agentsManagement.defaultAgentsCreated', 'Default agents created successfully'));
  //     }
  //   } catch (error) {
  //     console.error('Error initializing default agents:', error);
  //     toast.error(t('agentsManagement.failedToCreateDefaultAgents', 'Failed to create default agents'));
  //   }
  // };

  // Fetch agents
  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      // Fetch agents with proper column names
      const { data: agentData, error: agentError } = await supabase
        .from('ai_agents_a1b2c3d4e5')
        .select('*')
        .order('name');

      if (agentError) throw agentError;

      // Fetch all team assignments
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('team_allowed_agents_a1b2c3d4e5')
        .select('agent_id, team_id');

      if (assignmentError) throw assignmentError;

      // Map assignments to agents
      const agentTeamsMap = {};
      assignmentData.forEach(row => {
        if (!agentTeamsMap[row.agent_id]) {
          agentTeamsMap[row.agent_id] = [];
        }
        agentTeamsMap[row.agent_id].push(row.team_id);
      });

      // Enhance agents with assigned teams
      const enhancedAgents = (agentData || []).map(agent => ({
        ...agent,
        assigned_teams: agentTeamsMap[agent.id] || []
      }));

      setAgents(enhancedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error(t('agentsManagement.errorFetchingAgents', 'Error fetching agents'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast.error(t('agentsManagement.invalidImageFile', 'Please select a valid image file (PNG, JPG, GIF, or WebP)'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('agentsManagement.imageSizeTooLarge', 'Image must be less than 5MB'));
      return;
    }

    setImageFile(file);

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadAgentImage = async () => {
    if (!imageFile) return null;
    setImageUploading(true);

    try {
      // Generate a unique filename
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `agents/${agentForm.id || 'new'}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('assets')
        .upload(filePath, imageFile, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('assets')
        .getPublicUrl(filePath);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error('Failed to get public URL for uploaded image');
      }

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error('Error uploading agent image:', error);
      throw error;
    } finally {
      setImageUploading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!agentForm.name || !agentForm.webhook) {
      toast.error(t('agentsManagement.requiredFieldsMissing', 'Name and API endpoint are required'));
      return;
    }

    setLoading(true);

    try {
      // Upload image if selected
      let imageUrl = agentForm.image_url;
      if (imageFile) {
        imageUrl = await uploadAgentImage();
      }

      const agentId = agentForm.id || agentForm.name.toLowerCase().replace(/\s+/g, '-');

      // Create agent record
      const newAgent = {
        id: agentId,
        name: agentForm.name,
        description: agentForm.description,
        webhook: agentForm.webhook,
        icon: agentForm.icon,
        image_url: imageUrl,
        is_active: agentForm.is_active,
        training_data: agentForm.training_data,
        request_email: agentForm.request_email,
        created_at: new Date().toISOString(),
        created_by: user.id
      };

      const { error } = await supabase
        .from('ai_agents_a1b2c3d4e5')
        .insert([newAgent]);

      if (error) throw error;

      // Update agent image association if image was uploaded
      if (imageUrl) {
        const { error: imageError } = await supabase
          .from('agent_images_a1b2c3d4e5')
          .insert([{ agent_id: agentId, image_url: imageUrl }]);
          
        if (imageError) console.warn('Error creating agent image association:', imageError);
      }

      toast.success(t('agentsManagement.agentCreated', 'Agent created successfully'));
      setIsCreating(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error(t('agentsManagement.errorCreatingAgent', 'Error creating agent'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return;

    setLoading(true);

    try {
      // Upload image if selected
      let imageUrl = agentForm.image_url;
      if (imageFile) {
        imageUrl = await uploadAgentImage();
      }

      // Update agent record
      const { error } = await supabase
        .from('ai_agents_a1b2c3d4e5')
        .update({
          name: agentForm.name,
          description: agentForm.description,
          webhook: agentForm.webhook,
          icon: agentForm.icon,
          image_url: imageUrl,
          is_active: agentForm.is_active,
          training_data: agentForm.training_data,
          request_email: agentForm.request_email,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedAgent.id);

      if (error) throw error;

      // Update agent image association if image was uploaded
      if (imageUrl) {
        const { data: existingImage, error: checkError } = await supabase
          .from('agent_images_a1b2c3d4e5')
          .select('id')
          .eq('agent_id', selectedAgent.id)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') throw checkError;

        if (existingImage) {
          // Update existing image record
          const { error: updateImageError } = await supabase
            .from('agent_images_a1b2c3d4e5')
            .update({ image_url: imageUrl, updated_at: new Date().toISOString() })
            .eq('agent_id', selectedAgent.id);

          if (updateImageError) throw updateImageError;
        } else {
          // Create new image record
          const { error: insertImageError } = await supabase
            .from('agent_images_a1b2c3d4e5')
            .insert([{ agent_id: selectedAgent.id, image_url: imageUrl }]);

          if (insertImageError) throw insertImageError;
        }
      }

      toast.success(t('agentsManagement.agentUpdated', 'Agent updated successfully'));
      setIsEditing(false);
      setSelectedAgent(null);
      resetForm();
      fetchAgents();
    } catch (error) {
      console.error('Error updating agent:', error);
      toast.error(t('agentsManagement.errorUpdatingAgent', 'Error updating agent'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!confirm(t('agentsManagement.deleteAgentConfirm', 'Are you sure you want to delete this agent? This action cannot be undone.'))) {
      return;
    }

    setLoading(true);

    try {
      // Delete agent record
      const { error } = await supabase
        .from('ai_agents_a1b2c3d4e5')
        .delete()
        .eq('id', agentId);

      if (error) throw error;

      // Delete agent image association
      const { error: imageError } = await supabase
        .from('agent_images_a1b2c3d4e5')
        .delete()
        .eq('agent_id', agentId);

      // We don't throw on image deletion error as it's not critical
      if (imageError) console.warn('Error deleting agent image association:', imageError);

      toast.success(t('agentsManagement.agentDeleted', 'Agent deleted successfully'));
      if (selectedAgent?.id === agentId) {
        setSelectedAgent(null);
      }
      
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(t('agentsManagement.errorDeletingAgent', 'Error deleting agent'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setAgentForm({
      id: agent.id,
      name: agent.name,
      description: agent.description || '',
      webhook: agent.webhook,
      icon: agent.icon,
      image_url: agent.image_url || '',
      is_active: agent.is_active !== false,
      training_data: agent.training_data || '',
      request_email: agent.request_email || ''
    });
    setPreviewUrl(agent.image_url || '');
    setIsEditing(true);
    setIsCreating(false);
  };

  const resetForm = () => {
    setAgentForm({
      id: '',
      name: '',
      description: '',
      webhook: '',
      icon: '🤖',
      image_url: '',
      is_active: true,
      training_data: '',
      request_email: ''
    });
    setSelectedAgent(null);
    setIsCreating(false);
    setIsEditing(false);
    setImageFile(null);
    setPreviewUrl('');
  };

  // Filter agents based on search query
  const filteredAgents = agents.filter(agent =>
    (agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (searchTeam === "" || agent.assigned_teams?.includes(searchTeam))
  );

  if (!isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiSettings} className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('agentsManagement.accessDeniedTitle', 'Access Denied')}</h2>
          <p className="text-gray-600">{t('agentsManagement.accessDeniedMessage', 'You don\'t have permission to manage AI agents.')}</p>
        </div>
      </div>
     );
  }

  return (
      <div className="flex-1 overflow-y-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-12xl mx-auto"
        >
          {/* Header */}
          <div className="mb-8 flex items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {t('agentsManagement.title', 'AI Agents Management')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('agentsManagement.subtitle', 'Create, edit, and manage AI agents for your organization')}
              </p>
            </div>
            <Button
              onClick={() => {
                setSelectedAgent(null);
                setIsEditing(false);
                setIsCreating(true);
                setAgentForm({
                  id: '',
                  name: '',
                  description: '',
                  webhook: '',
                  icon: '🤖',
                  image_url: '',
                  is_active: true,
                  training_data: '',
                  request_email: ''
                });
                setImageFile(null);
                setPreviewUrl('');
              }}
              icon={FiPlus}
              responsive
            >
              {t('agentsManagement.addNewAgent', 'Add New Agent')}
            </Button>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t('agentsManagement.searchAgentsPlaceholder', 'Search agents...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
              <SafeIcon
                icon={FiSettings}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              />
            </div>
            <select
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setSearchTeam(e.target.value)}
              value={searchTeam}
            >
              <option value="">{t('agentsManagement.allTeams', 'All Teams')}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>

          {/* Agents Grid */}
          {loading && agents.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">{t('agentsManagement.loadingAgents', 'Loading agents...')}</p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiSettings} className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {agents.length === 0 ? t('agentsManagement.noAgentsCreated', 'No AI agents created yet') : t('agentsManagement.noAgentsFound', 'No agents found')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {agents.length === 0 ? t('agentsManagement.createFirstAgentMessage', 'Create your first AI agent to get started.') : t('agentsManagement.adjustSearchCriteria', 'Try adjusting your search criteria.')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xs:grid-cols-3 gap-6">
              {filteredAgents.map((agent, index) => (
                <motion.div
                  key={agent.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${agent.image_url ? 'bg-white' : 'bg-blue-500'} flex items-center justify-center text-white text-xl shadow-lg overflow-hidden`}>
                      {agent.image_url ? (
                        <img
                          src={agent.image_url}
                          alt={agent.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, image_url: null } : a));
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        agent.icon
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        agent.is_active !== false 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}>
                        <SafeIcon icon={agent.is_active !== false ? FiCheck : FiClock} className="w-3 h-3 mr-1" />
                        {agent.is_active !== false ? t('agentsManagement.statusActive', 'Active') : t('agentsManagement.statusInactive', 'Inactive')}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {agent.name}
                      </h3>
                      {agent.assigned_teams && agent.assigned_teams.length > 0 ? (
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          {agent.assigned_teams.map(id => teams.find(t => t.id === id)?.name).filter(Boolean).join(', ')}
                        </p>
                      ) : (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {t('agentsManagement.noAssignedTeams','Aucune équipe assignée')}
                        </p>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed line-clamp-2">
                      {agent.description || t('agentsManagement.noDescription', 'No description provided')}
                    </p>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <SafeIcon icon={FiGlobe} className="w-3 h-3 mr-1" />
                      <span className="truncate">{agent.webhook}</span>
                    </div>
                    {agent.request_email && (
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                        <span className="truncate">{agent.request_email}</span>
                      </div>
                    )}
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                      <span>{t('agentsManagement.createdAt', 'Created')} : {agent.created_at ? new Date(agent.created_at).toLocaleDateString(user?.language || 'en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : t('agentsManagement.unknownDate', 'Unknown')}</span>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end space-x-2">
                    <Button
                      onClick={() => handleEditAgent(agent)}
                      className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={t('agentsManagement.editAgent', 'Edit Agent')}
                      icon={FiEdit2}
                      iconClassName='w-4 h-4'
                    />
                    <Button
                      onClick={() => handleDeleteAgent(agent.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={t('agentsManagement.deleteAgent', 'Delete Agent')}
                      icon={FiTrash2}
                      iconClassName='w-4 h-4'
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Statistics */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiSettings} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentsManagement.totalAgents', 'Total Agents')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{agents.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentsManagement.activeAgents', 'Active Agents')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {agents.filter(a => a.is_active !== false).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiSettings} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentsManagement.teams', 'Teams')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{teams.length}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

      {/* Modal for Create/Edit Agent */}
      {(isCreating || isEditing) && (
        <Modal isOpen={true} onClose={resetForm} title={isCreating ? t('agentsManagement.addNewAgentTitle', 'Add New AI Agent') : t('agentsManagement.editAgentTitle', 'Edit Agent')}>
            <form onSubmit={isCreating ? handleCreateAgent : handleUpdateAgent}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Agent ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('agentsManagement.agentIdLabel', 'Agent ID *')}
                    </label>
                    <input
                      type="text"
                      value={agentForm.id}
                      onChange={(e) => setAgentForm({ ...agentForm, id: e.target.value })}
                      disabled={isEditing}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('agentsManagement.agentIdPlaceholder', 'agent-id (auto-generated from name if empty)')}
                    />
                  </div>
                  
                  {/* Agent Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('agentsManagement.agentNameLabel', 'Agent Name *')}
                    </label>
                    <input
                      type="text"
                      value={agentForm.name}
                      onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('agentsManagement.agentNamePlaceholder', 'e.g. Sales Assistant')}
                    />
                  </div>
                </div>
                
                {/* Description */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('agentsManagement.descriptionLabel', 'Description')}
                  </label>
                  <textarea
                    value={agentForm.description}
                    onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })}
                    rows="3"
                    placeholder={t('agentsManagement.descriptionPlaceholder', 'Describe what this agent does...')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div className="mt-6">
                  {/* API Endpoint */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('agentsManagement.apiEndpointLabel', 'API Endpoint *')}
                    </label>
                    <input
                      type="url"
                      value={agentForm.webhook}
                      onChange={(e) => setAgentForm({ ...agentForm, webhook: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('agentsManagement.apiEndpointPlaceholder', 'e.g. api/agents/sales-assistant')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 mt-6">                  
                  {/* Request Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('agentsManagement.requestEmailLabel', 'Request Email')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <SafeIcon 
                          icon={FiMail} 
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        />
                        <input
                          type="email"
                          value={agentForm.request_email}
                          onChange={(e) => setAgentForm({ ...agentForm, request_email: e.target.value })}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder={t('agentsManagement.requestEmailPlaceholder', 'agent-specific@example.com')}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('agentsManagement.requestEmailDescription', 'Email address for user requests related to this agent. If not specified, default email will be used.')}
                    </p>
                  </div>                 
                </div>
                
                {/* Agent Image Upload */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('agentsManagement.agentImageLabel', 'Agent Image')}
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className={`w-16 h-16 rounded-lg ${!previewUrl ? 'bg-blue-500' : ''} flex items-center justify-center text-white overflow-hidden border border-gray-200`}>
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={t('agentsManagement.agentPreviewAlt', 'Agent preview')}
                          className="w-full h-full object-cover"
                          onError={() => setPreviewUrl('')}
                        />
                      ) : (
                        <span className="text-2xl">{agentForm.icon}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        id="agent-image"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="agent-image"
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                      >
                        <SafeIcon icon={FiUpload} className="w-4 h-4" />
                        <span>{t('agentsManagement.uploadImage', 'Upload Image')}</span>
                      </label>
                      {imageFile && (
                        <p className="text-xs text-gray-500 mt-1">
                          {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Set as Active */}
                <div className="mt-6 flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={agentForm.is_active}
                    onChange={(e) => setAgentForm({ ...agentForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-xs"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    {t('agentsManagement.setActiveLabel', 'Set agent as active immediately')}
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('agentsManagement.setActiveDescription', 'If unchecked, agent will be created in "In Training" mode')}
                </p>
                
                {/* Action Buttons */}
                <div className="mt-8 flex justify-end space-x-3">
                  <Button
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('agentsManagement.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || imageUploading}
                    loading={loading || imageUploading}
                    icon={FiSave}
                  >
                    {isCreating ? t('agentsManagement.createAgent', 'Create Agent') : t('agentsManagement.updateAgent', 'Update Agent')}
                  </Button>
                </div>
              </form> 
            </Modal>
      )}
    </div>
  );
};

export default AgentsManagementPage;